import { mapActions, mapGetters } from 'vuex';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { differenceInSeconds } from 'date-fns';
import { VITE_ENV_DEVELOPMENT, VITE_ENV_REVIEW, VITE_ENV_STAGING } from '@/app/data/general_constants';

const profile_mixin = {
  props: {
    project_id: { type: String, default: null },
  },

  data() {
    return {
      refresh_app: false,
      fetch_client_at: null,
      fetch_feature_toggles_at: null,
      fetch_profile_at: null,
      PROFILE_TYPES,
      VITE_ENV_DEVELOPMENT,
      VITE_ENV_STAGING,
      VITE_ENV_REVIEW,
      projects: {},
      initialized: false,
    };
  },

  computed: {
    ...mapGetters([
      'current_client',
      'current_is_impersonation',
      'current_profile',
      'current_profile_true',
      'sidebar_type',
      'billings',
    ]),

    environment() {
      return import.meta.env.VITE_ENV;
    },

    billing_current_client() {
      if (this.current_profile.type === PROFILE_TYPES.SUPERVISOR) return null;
      return _.find(this.billings, ['attributes.client_id', this.current_client?.id]);
    },

    favicon_url() {
      return _.includes([PROFILE_TYPES.SUPERVISOR], this.current_profile.type)
        ? null
        : this.current_client.attributes.picture_favicon_url;
    },
  },

  created() {
    this.emitter.$on('fetchClient', this.fetchClient);
    this.emitter.$on('fetchProfile', this.fetchProfile);
    this.emitter.$on('fetchFeatureToggles', this.fetchFeatureToggles);
  },

  methods: {
    ...mapActions([
      'FETCH_CLIENT',
      'FETCH_PROJECTS',
      'FETCH_USER_PROFILE',
      'FETCH_FEATURE_TOGGLES',
      'INIT_DOCUMENTS_PREFS',
    ]),

    async initProfile() {
      await Promise.all([
        this.current_profile.type !== PROFILE_TYPES.SUPERVISOR ? this.fetchClient() : null,
        this.current_profile.type !== PROFILE_TYPES.SUPERVISOR ? this.fetchProjects() : null,
        this.current_profile.type === PROFILE_TYPES.CUSTOMER ? this.fetchUnits() : null,
      ]);
      if (this.current_profile.type !== PROFILE_TYPES.SUPERVISOR) {
        this.fetchDocumentsPrefs();
      }
      this.emitter.$emit('initTitle');
      this.emitter.$emit('initFavicon', this.favicon_url);
      if (_.get(this.current_profile, 'type') === PROFILE_TYPES.CUSTOMER) {
        this.emitter.$emit('initPWA');
      }
      this.emitter.$emit('initIntercom');
      this.emitter.$emit('initRecording');
      this.initialized = true;
    },

    async fetchClient() {
      // locking mechanism to avoid concurrent fetches, with auto-release after 60s (on error)
      if (differenceInSeconds(this.fetch_client_at, new Date()) > 60) this.fetch_client_at = null;
      if (this.initialized && this.fetch_client_at) return;
      this.fetch_client_at = new Date();
      await this.FETCH_CLIENT({ id: this.current_profile.attributes.client_id });
      this.fetch_client_at = null;
    },

    async fetchFeatureToggles() {
      if (differenceInSeconds(this.fetch_feature_toggles_at, new Date()) > 60) this.fetch_feature_toggles_at = null;
      if (this.initialized && this.fetch_feature_toggles_at) return;
      this.fetch_feature_toggles_at = new Date();
      await this.FETCH_FEATURE_TOGGLES({});
      this.fetch_feature_toggles_at = null;
    },

    async fetchProfile() {
      // locking mechanism to avoid concurrent fetches, with fail-safe auto-release after 60s
      if (differenceInSeconds(this.fetch_profile_at, new Date()) > 60) this.fetch_profile_at = null;
      if (this.initialized && this.fetch_profile_at) return;
      this.fetch_profile_at = new Date();
      await this.FETCH_USER_PROFILE({});
      this.fetch_profile_at = null;
    },

    async fetchProjects() {
      // vuex getters are not guaranteed to be initialized, thus saving via data variable instead
      const projects = await this.FETCH_PROJECTS({ skip_customer: false, followed: true });
      this.projects = _.keyBy(projects, 'id');
    },

    async fetchDocumentsPrefs() {
      await this.INIT_DOCUMENTS_PREFS({});
    },
  },
};

export default profile_mixin;
